import {
  ArrowRight,
  Award,
  BookOpen,
  Briefcase,
  ClipboardCheck,
  Cpu,
  ExternalLink,
  FileText,
  GraduationCap,
  Landmark,
  LoaderCircle,
  Scale,
  School,
  Sparkles,
  TrendingUp,
  Trophy,
  X,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { partnersApi } from '../api/endpoints'
import { useAuth } from '../auth/AuthContext'
import { FourCScore } from './FourCScore'
import { VioletDotsCertification } from './VioletDotsCertification'
import { openHoldingTab } from '../utils/holdingTab'
import type { NdliHandoffResponse } from '../types/api'

type PickerKind = 'cat' | 'apply'

interface PartnerLink {
  label: string
  href: string
  icon: ReactNode
  /**
   * Set when pressing this asks our own server where to go first, rather than following href.
   *
   * <p>href stays as the destination to fall back to, so a link that needs a hand-off still has
   * somewhere to send people when the hand-off cannot be made.
   */
  handoff?: 'talevate' | 'mysathi' | 'sathiNao'
  /** A page of this app rather than a partner's - opened in place, not in a new tab. */
  to?: string
  /** Set when pressing this asks which option first - see PICKERS and StreamPickerDialog. */
  picker?: PickerKind
}

const LINKS: PartnerLink[] = [
  {
    label: 'Free Coaching',
    href: 'https://www.careerlauncher.com/cat-mba/open-cat/',
    icon: <GraduationCap size={16} />,
    // Asks MBA / LAW / CUET / IPM first; MBA registers them with NDLI and signs them into MyZone.
    picker: 'cat',
  },
  {
    label: 'Fill College Applications',
    href: 'https://www.careerlauncher.com/easy-apply/',
    icon: <FileText size={16} />,
    // Asks the course first; each registers through NDLI's myzone-registration with target EASY_APPLY.
    picker: 'apply',
  },
  {
    label: 'SATHI NAO Assessment',
    href: 'https://myzone.careerlauncher.com/sis/index.jsp',
    icon: <Trophy size={16} />,
    // Straight to NDLI, no question first: registers them (prodCat SPORTS) and signs them into
    // MyZone with &redirectTo=/sis/index.jsp - see NdliService.
    handoff: 'sathiNao',
  },
  {
    label: 'Jobs / Internship',
    href: 'https://talevate.in/en',
    icon: <Briefcase size={16} />,
    handoff: 'talevate',
  },
  {
    label: 'Online Degree',
    href: 'https://www.careerlauncher.com/onlinedegree/',
    icon: <Award size={16} />,
  },
]

/**
 * Partner links in the empty space under the nav.
 *
 * <p>Plain text rows rather than logo tiles: a learner should be able to tell what pressing one
 * does before they leave Yellow Dot, not just recognise a brand mark. Each still opens in a new
 * tab so the rail stays put underneath.
 *
 * <p>"Jobs / Internship" is not a plain link any more. Pressing it asks our server for a one-time
 * TalEvate auto-login link - registering the participant as a candidate there if this is their
 * first time - so they arrive already signed in rather than at a login form for an account they do
 * not know they have. The credentials that buys stay on the server; this only ever sees a URL.
 */

/**
 * Signs into MyZone with NDLI's link and token. They only work as a pair, and a browser cannot put a
 * header on a navigation, so the token goes as a POSTed form field - which also keeps it out of the
 * URL, history and Referer, as NDLI asks.
 */
function postSignIn(target: Window | null, url: string, token: string) {
  const doc = target ? target.document : document
  const form = doc.createElement('form')
  form.method = 'POST'
  form.action = url
  const field = doc.createElement('input')
  field.type = 'hidden'
  field.name = 'token'
  field.value = token
  form.appendChild(field)
  doc.body.appendChild(form)
  form.submit()
}

interface PickerOption {
  /** Sent to the server as the path segment, lower-cased - see PartnerController. */
  id: string
  title: string
  detail: string
  icon: ReactNode
}

interface PickerConfig {
  heading: string
  subheading: string
  action: string
  options: PickerOption[]
  handoff: (id: string) => Promise<NdliHandoffResponse>
}

/**
 * The sidebar links that ask a question before leaving Yellow Dot. Each registers the student
 * through NDLI for the option picked and signs them in; they differ in the endpoint behind handoff
 * (and so the target it sends) and in what the options are.
 */
const PICKERS: Record<PickerKind, PickerConfig> = {
  cat: {
    heading: 'Start learning now',
    subheading: 'Choose the exam you are preparing for.',
    action: 'Start learning',
    options: [
      { id: 'MBA', title: 'MBA', detail: 'CAT, XAT, SNAP, NMAT', icon: <TrendingUp size={18} /> },
      { id: 'LAW', title: 'Law', detail: 'CLAT, AILET', icon: <Scale size={18} /> },
      { id: 'CUET', title: 'CUET', detail: 'Central university UG', icon: <BookOpen size={18} /> },
      { id: 'IPM', title: 'IPM', detail: 'IPMAT Indore, Rohtak', icon: <Landmark size={18} /> },
    ],
    handoff: (id) => partnersApi.ndliHandoff(id),
  },
  apply: {
    heading: 'Fill college applications',
    subheading: 'Choose the course you are applying for.',
    action: 'Apply now',
    options: [
      { id: 'MBA', title: 'MBA', detail: 'MBA and PGDM colleges', icon: <TrendingUp size={18} /> },
      { id: 'CUET', title: 'CUET', detail: 'Central and state universities', icon: <BookOpen size={18} /> },
      { id: 'LAW', title: 'Law', detail: 'Five-year LLB programmes', icon: <Scale size={18} /> },
      { id: 'AFTER-12', title: 'After 12', detail: 'BBA, BMS, BCA and more', icon: <School size={18} /> },
      { id: 'ENGG', title: 'Engineering', detail: 'B.Tech and BE colleges', icon: <Cpu size={18} /> },
    ],
    handoff: (id) => partnersApi.ndliEasyApplyHandoff(id),
  },
}

/**
 * Asks which option first, as cards, then registers the student through NDLI for it and signs them
 * in on a new tab. fallbackUrl is where they go if the request itself does not land.
 */
function StreamPickerDialog({
  config,
  tabTitle,
  fallbackUrl,
  onClose,
}: {
  config: PickerConfig
  tabTitle: string
  fallbackUrl: string
  onClose: () => void
}) {
  const [chosen, setChosen] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  async function start(id: string) {
    if (pending) return
    setChosen(id)
    setPending(true)
    const tab = openHoldingTab(tabTitle)
    try {
      const { url, token } = await config.handoff(id)
      if (token) postSignIn(tab, url, token)
      else if (tab) tab.location.href = url
      else window.location.href = url
      onClose()
    } catch (err) {
      // The server already falls back to the partner's own page rather than failing, so reaching
      // here means the request itself did not land. That page is still the right place to go.
      console.error(`[partners] NDLI ${id} hand-off failed`, err)
      if (tab) tab.location.href = fallbackUrl
      else window.location.href = fallbackUrl
      onClose()
    } finally {
      setPending(false)
    }
  }

  // Compact on purpose: the whole dialog should fit a laptop screen without scrolling. Cards are a
  // row each (icon beside the text), one column on a phone, two from 420px; five cards go three
  // across on wider screens rather than leave one alone on a row.
  const wide = config.options.length > 4
  const columns = wide ? 'grid-cols-1 min-[420px]:grid-cols-2 md:grid-cols-3' : 'grid-cols-1 min-[420px]:grid-cols-2'

  return (
    <div
      className="fixed inset-0 z-[1200] grid place-items-center overflow-y-auto bg-ink-900/50 p-3 backdrop-blur-[2px] sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="stream-picker-title"
      onClick={(e) => e.target === e.currentTarget && !pending && onClose()}
    >
      <div
        className={`card relative max-h-[calc(100dvh-1.5rem)] w-full overflow-hidden overflow-y-auto bg-white shadow-float ${
          wide ? 'max-w-3xl' : 'max-w-xl'
        }`}
      >
        {/* Header on the warm brand glow, so the dialog reads as ours rather than a system prompt. */}
        <div className="relative bg-dot-hero px-4 py-4 pr-12 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            aria-label="Close"
            className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full text-ink-500 transition hover:bg-white/70 hover:text-ink-900 disabled:opacity-50"
          >
            <X size={16} />
          </button>
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-sun-400 text-ink-900 shadow-card">
              <GraduationCap size={18} />
            </span>
            <div className="min-w-0">
              <h2 id="stream-picker-title" className="font-display text-lg font-semibold leading-tight text-ink-900 sm:text-xl">
                {config.heading}
              </h2>
              <p className="text-xs text-ink-500 sm:text-sm">{config.subheading}</p>
            </div>
          </div>
        </div>

        <div
          role="tablist"
          aria-label={config.heading}
          className={`grid gap-2.5 px-4 pt-4 sm:gap-3 sm:px-6 ${columns}`}
        >
          {config.options.map((o) => {
            const selected = chosen === o.id
            const loading = pending && selected
            return (
              <button
                key={o.id}
                type="button"
                role="tab"
                aria-selected={selected}
                disabled={pending}
                onClick={() => start(o.id)}
                className={`group flex items-center gap-3 rounded-xl border p-3 text-left transition ${
                  selected
                    ? 'border-sun-400 bg-sun-300/15 shadow-card ring-1 ring-sun-400'
                    : 'border-ink-200/70 bg-white hover:border-sun-300 hover:shadow-card'
                } disabled:cursor-wait`}
              >
                <span
                  className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg transition ${
                    selected ? 'bg-sun-400 text-ink-900' : 'bg-ink-100 text-ink-700 group-hover:bg-sun-300/60'
                  }`}
                >
                  {o.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold uppercase leading-tight text-ink-900">{o.title}</span>
                  <span className="mt-0.5 block text-xs leading-snug text-ink-500">{o.detail}</span>
                  <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-sun-600">
                    {loading ? (
                      <>
                        <LoaderCircle size={13} className="animate-spin" /> Signing you in…
                      </>
                    ) : (
                      <>
                        {config.action} <ArrowRight size={13} className="transition group-hover:translate-x-0.5" />
                      </>
                    )}
                  </span>
                </span>
              </button>
            )
          })}
        </div>

        <p className="px-4 pb-4 pt-3 text-xs text-ink-500 sm:px-6" role="status">
          This opens in a new tab, and you will be signed in automatically.
        </p>
      </div>
    </div>
  )
}

/**
 * Opening a partner link that needs our server first: which one is in flight, and the opener. Shared
 * by the "Explore more" rows and the Certification page under Profile, so both behave the same.
 */
function usePartnerHandoff() {
  const [pending, setPending] = useState<string | null>(null)

  async function openWithHandoff(link: PartnerLink) {
    if (pending) return
    setPending(link.label)

    const tab = openHoldingTab(link.label)
    try {
      if (link.handoff === 'sathiNao') {
        // NDLI signs in with a link and token together, POSTed - as the Free Coaching picker does.
        const { url, token } = await partnersApi.ndliSathiNaoHandoff()
        if (token) postSignIn(tab, url, token)
        else if (tab) tab.location.href = url
        else window.location.href = url
        return
      }
      const { url } =
        link.handoff === 'mysathi' ? await partnersApi.mysathiHandoff() : await partnersApi.talevateHandoff()
      if (tab) tab.location.href = url
      // A blocked tab is not a reason to strand them: send this one instead. The link is single-use
      // and this is its only use, so nothing is wasted.
      else window.location.href = url
    } catch (err) {
      // The server already falls back to TalEvate's own page rather than failing, so reaching here
      // means the request itself did not land - offline, or the session expired. Their own site is
      // still the right destination; they will simply be asked to sign in.
      console.error(`[partners] ${link.label} hand-off failed`, err)
      if (tab) tab.location.href = link.href
      else window.location.href = link.href
    } finally {
      setPending(null)
    }
  }

  return { pending, openWithHandoff }
}

const rowClass =
  'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-ink-600 transition hover:bg-ink-100 disabled:opacity-60 [@media(max-height:900px)]:py-1.5'

/** One partner row: a picker, a server hand-off, or a plain link - each opening in a new tab. */
function PartnerRow({
  link,
  pending,
  onHandoff,
  onPicker,
}: {
  link: PartnerLink
  pending: string | null
  onHandoff: (link: PartnerLink) => void
  onPicker: (link: PartnerLink) => void
}) {
  const l = link
  if (l.to) {
    return (
      <Link to={l.to} title={l.label} className={rowClass}>
        <span className="shrink-0 text-ink-400">{l.icon}</span>
        <span className="min-w-0 flex-1 truncate">{l.label}</span>
      </Link>
    )
  }
  if (l.picker) {
    return (
      <button type="button" onClick={() => onPicker(l)} aria-haspopup="dialog" title={l.label} className={rowClass}>
        <span className="shrink-0 text-ink-400">{l.icon}</span>
        <span className="min-w-0 flex-1 truncate">{l.label}</span>
        <ExternalLink size={12} className="shrink-0 text-ink-300" aria-hidden="true" />
      </button>
    )
  }
  if (l.handoff) {
    return (
      <button
        type="button"
        onClick={() => onHandoff(l)}
        disabled={pending === l.label}
        aria-label={`${l.label} (opens in a new tab)`}
        // The label truncates at this width (see the ellipsis on "Fill College Applicat...");
        // the native title tooltip is the cheapest way to recover the full text on hover.
        title={l.label}
        className={rowClass}
      >
        <span className="shrink-0 text-ink-400">{l.icon}</span>
        <span className="min-w-0 flex-1 truncate">{pending === l.label ? 'Opening...' : l.label}</span>
        <ExternalLink size={12} className="shrink-0 text-ink-300" aria-hidden="true" />
      </button>
    )
  }
  return (
    <a
      href={l.href}
      target="_blank"
      // noopener stops the opened page reaching back through window.opener; noreferrer keeps
      // our URL, which can carry a participant id, out of their analytics.
      rel="noopener noreferrer"
      aria-label={`${l.label} (opens in a new tab)`}
      title={l.label}
      className={rowClass}
    >
      <span className="shrink-0 text-ink-400">{l.icon}</span>
      <span className="min-w-0 flex-1 truncate">{l.label}</span>
      <ExternalLink size={12} className="shrink-0 text-ink-300" aria-hidden="true" />
    </a>
  )
}

/**
 * The Certification page's two cards. Carries its own icon/href/handoff rather than looking them
 * up in LINKS (the "Explore more" rail) - the rail no longer lists these two, but this page, whose
 * whole purpose is these two cards, still must.
 */
const CREDENTIALS: (PartnerLink & {
  title: string
  detail: string
  action: string
  /** The button once a score is showing - the same link opens their report on mySATHI. */
  actionWhenScored?: string
})[] = [
  {
    label: 'Take 4C Assessment',
    title: 'Take 4C Assessment',
    detail: 'Measure your Communication, Collaboration, Critical thinking and Creativity with mySATHI.',
    action: 'Start assessment',
    actionWhenScored: 'Access your detailed report',
    href: 'https://student.mysathi.org/MSCR/LoginAction_input.action',
    icon: <ClipboardCheck size={16} />,
    // Registers them with mySATHI by mobile number first - see MysathiService.
    handoff: 'mysathi',
  },
  {
    label: 'AI Certification',
    title: 'AI Certification',
    detail: 'Learn practical AI skills and earn a certificate with Violet Dots.',
    action: 'Get certified',
    href: 'https://www.violetdots.ai',
    icon: <Sparkles size={16} />,
  },
]

/**
 * The Certification page's cards: Take 4C Assessment (with the score mySATHI holds for them) and AI
 * Certification.
 */
export function CredentialCards() {
  const { pending, openWithHandoff } = usePartnerHandoff()
  // Whether the 4C card is showing a score - its button then opens the report, not the test.
  const [fourCScored, setFourCScored] = useState(false)

  return (
    <div className="grid items-start gap-4 sm:grid-cols-2">
      {CREDENTIALS.map((c) => {
        const busy = pending === c.label
        const action = busy ? (
          <>
            <LoaderCircle size={15} className="animate-spin" /> Opening...
          </>
        ) : (
          <>
            {(fourCScored && c.actionWhenScored) || c.action} <ArrowRight size={15} />
          </>
        )
        const buttonClass = 'btn-sun mt-4 gap-1.5 disabled:cursor-wait'
        return (
          <div key={c.label} className="card flex flex-col items-start p-5">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-sun-300/60 text-ink-900 [&>svg]:h-6 [&>svg]:w-6">
              {c.icon}
            </span>
            <h2 className="mt-4 text-lg font-bold text-ink-900">{c.title}</h2>
            <p className="mt-1 text-sm leading-relaxed text-ink-500">{c.detail}</p>
            {c.handoff === 'mysathi' && <FourCScore onScored={setFourCScored} />}
            {c.label === 'AI Certification' ? (
              // Its own body: certificates, the questions Violet Dots needs answered, and a signed hand-off.
              <VioletDotsCertification />
            ) : c.handoff ? (
              <button
                type="button"
                onClick={() => openWithHandoff(c)}
                disabled={busy}
                aria-label={`${c.title} (opens in a new tab)`}
                className={buttonClass}
              >
                {action}
              </button>
            ) : (
              <a
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${c.title} (opens in a new tab)`}
                className={buttonClass}
              >
                {action}
              </a>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function SidebarBanners() {
  const { pending, openWithHandoff } = usePartnerHandoff()
  const [picker, setPicker] = useState<PartnerLink | null>(null)
  // SATHI NAO is only for a student whose needs include Sports & Fitness, and only where it is
  // switched on - the server decides both. Hidden until it says yes, and asked again whenever the
  // profile changes, so adding or dropping that need shows or hides it without a reload.
  const { profile } = useAuth()
  const [sathiNaoEnabled, setSathiNaoEnabled] = useState(false)

  useEffect(() => {
    let cancelled = false
    partnersApi
      .sathiNaoAvailability()
      .then((r) => !cancelled && setSathiNaoEnabled(r.available === true))
      .catch(() => !cancelled && setSathiNaoEnabled(false))
    return () => {
      cancelled = true
    }
  }, [profile])

  const links = LINKS.filter((l) => l.handoff !== 'sathiNao' || sathiNaoEnabled)

  return (
    <div className="border-t border-ink-200/70 pt-3">
      <div className="pb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400 [@media(max-height:760px)]:hidden">
        Explore more
      </div>
      <div className="space-y-1 [@media(max-height:900px)]:space-y-0.5">
        {links.map((l) => (
          <PartnerRow key={l.href} link={l} pending={pending} onHandoff={openWithHandoff} onPicker={setPicker} />
        ))}
      </div>
      {picker?.picker && (
        <StreamPickerDialog
          config={PICKERS[picker.picker]}
          tabTitle={picker.label}
          fallbackUrl={picker.href}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  )
}
